import { Injectable } from '@angular/core';
import { DashboardState } from '@shared/models/interfaces';
import { BehaviorSubject } from 'rxjs';


@Injectable({
  providedIn: 'root'
})
export class DashboardStateService {
  defaultState: DashboardState = {
    filter: 'all',
    currentView: 'dayGridWeek',
    dateRange: new Date().toDateString()
  };
  private DASHBOARD_FILTER_OPTIONS_KEY = 'DashboardFilterOptions';
  private DASHBOARD_FILTER_STATE_KEY = 'DashboardFilterState';
  private extraResetKeys: string[] = [];

  private dashboardFilterStateSubject: BehaviorSubject<DashboardState>;
  filterState$;

  private dashboardFilterOptionsSubject: BehaviorSubject<number[]>;
  filterOptions$;

  constructor() {
    this.dashboardFilterStateSubject = new BehaviorSubject<DashboardState>(this.readDashboardFilterState());
    this.filterState$ = this.dashboardFilterStateSubject.asObservable();

    this.dashboardFilterOptionsSubject = new BehaviorSubject<number[]>(this.readDashboardFilterOptions());
    this.filterOptions$ = this.dashboardFilterOptionsSubject.asObservable();
  }

  setFilterState(state: DashboardState) {
    const normalized = this.normalizeState(state);
    this.dashboardFilterStateSubject.next(normalized);
    try {
      sessionStorage.setItem(this.DASHBOARD_FILTER_STATE_KEY, JSON.stringify(normalized));
    } catch { }
  }

  getFilterState(): DashboardState {
    return this.dashboardFilterStateSubject.value;
  }

  setFilterOptions(options: number[]) {
    this.dashboardFilterOptionsSubject.next(options);
    try {
      sessionStorage.setItem(this.DASHBOARD_FILTER_OPTIONS_KEY, JSON.stringify(options));
    } catch { }
  }

  getFilterOptions(): number[] {
    return this.dashboardFilterOptionsSubject.value;
  }

  resetState() {
    this.dashboardFilterStateSubject.next(this.defaultState);
    this.dashboardFilterOptionsSubject.next([]);
    sessionStorage.removeItem(this.DASHBOARD_FILTER_STATE_KEY);
    sessionStorage.removeItem(this.DASHBOARD_FILTER_OPTIONS_KEY);

    // Clean any extra feature-specific keys
    this.extraResetKeys?.forEach((key) => {
      try {
        sessionStorage.removeItem(key);
      } catch { }
    });
  }

  private readDashboardFilterState(): DashboardState {
    const raw = sessionStorage.getItem(this.DASHBOARD_FILTER_STATE_KEY);
    if (!raw) return this.defaultState;
    try {
      const parsed = JSON.parse(raw) as DashboardState;
      return this.normalizeState(parsed);
    } catch {
      sessionStorage.removeItem(this.DASHBOARD_FILTER_STATE_KEY);
      return this.defaultState;
    }
  }

  private readDashboardFilterOptions(): number[] {
    const raw = sessionStorage.getItem(this.DASHBOARD_FILTER_OPTIONS_KEY);
    if (!raw) return [];
    try {
      return JSON.parse(raw) as number[];
    } catch {
      sessionStorage.removeItem(this.DASHBOARD_FILTER_OPTIONS_KEY);
      return [];
    }
  }

  private normalizeState(state: DashboardState): DashboardState {
    const cleanCsv = (s: string | undefined | null) =>
      (s ?? '')
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean)
        .join(',');

    return {
      filter: cleanCsv(state.filter),
      currentView: state.currentView,
      dateRange: state.dateRange
    };
  }
}

